"use client";

import { useState, useEffect, useRef } from "react";
import { Post } from "@/types";
import CommentList from "../comment/CommentList";
import ImagePreview from "../ImagePreview";
import { useComment } from "@/hooks/useComment";
import useLogin from "@/hooks/useLogin";
import CommentInput from "../comment/CommentInput";
import useDocStore from "@/models/doc";
import { likeDocService, unLikeDocService } from "@/services/post";
import { followUserService, unfollowUserService } from "@/services/user";
import { useStatus } from "@/hooks/useStatus";
import { track } from "@/lib/log";
import clsx from "clsx";

// up: number;//原始点赞数
// isLiked: boolean;//是否之前点赞,在什么时机进行初始化？
// isNowLiked: boolean;//是否现在点赞
interface PostModalProps {
  post: Post & { up: number, isLiked: boolean, isNowLiked: boolean } | null;
  isOpen: boolean;
  onClose: () => void;
  onLike?: (post: Post & { isNowLiked?: boolean, isLiked?: boolean }) => void;
  detail?: boolean;
  qualityComments?: any[];
}


export default function PostModal({ post, isOpen, onClose, onLike, detail = false, qualityComments = [] }: PostModalProps) {
  const { liked, toggleLiked } = useStatus({ docid: post?.docid, media_id: post?.media_id });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [likeCount, setLikeCount] = useState(post?.up || 0);
  const [isNowLiked, setIsNowLiked] = useState<boolean>(post?.isNowLiked || false);
  const { setDoc } = useDocStore();
  const { checkLogin } = useLogin();
  function changeLikeCount(isLiked: boolean, isNowLiked: boolean, up: number) {
    setLikeCount(() => {
      let res = up
      if (isLiked && !isNowLiked) {
        res = up - 1;//点赞-1
      } else if (!isLiked && isNowLiked) {
        res = up + 1;//点赞+1
      } else {
        res = up;
      }
      return res;
    })
  }
  useEffect(() => {
    changeLikeCount(post?.isLiked || false, isNowLiked, post?.up || 0);
  }, [isNowLiked, post?.isLiked, post?.up]);
  useEffect(() => {
    if (post) {
      setCurrentImageIndex(0);
      setDoc({
        docid: post.doc_id,
        title: post.title,
        content: post.content,
        authorId: post.author.id,
        tags: post.tags,
        images: post.images
      });
    }
  }, [post]);
  // Use esc to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      onLike?.({ ...post, followed: isFollowed ? 1 : 0, isNowLiked } as Post);
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  // Use keyboard left and right arrows to switch images
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrevImage();
        track("switch_image", {
          action: "prev_image",
          doc_id: post?.docid,
          type: "keyboard",
          page: "detail"
        })
      }
      if (e.key === "ArrowRight") {
        handleNextImage();
        track("switch_image", {
          action: "next_image",
          doc_id: post?.docid,
          type: "keyboard",
          page: "detail"
        })
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, post]);

  const handlePrevImage = () => {
    if (!post) return;
    setCurrentImageIndex((prev) =>
      Math.max(0, prev - 1)
    );
  };

  const handleNextImage = () => {
    if (!post) return;
    setCurrentImageIndex((prev) =>
      Math.min(post.images.length - 1, prev + 1)
    );
  };



  const { postComment } = useComment(post?.docid!);
  const handleLike = checkLogin(() => {
    setIsNowLiked(e => !e);
    toggleLiked(post?.docid!);
    if (liked) {
      unLikeDocService({ docid: post?.docid });
    } else {
      likeDocService({ docid: post?.docid });
    }
  });
  const [isFollowed, setIsFollowed] = useState(post?.followed == 1);
  useEffect(() => {
    if (post) {
      setIsFollowed(post.followed == 1);
    }
  }, [post]);

  // 同步 isNowLiked 状态
  useEffect(() => {
    if (post) {
      setIsNowLiked(post.isNowLiked || false);
    }
  }, [post]);
  const handleFollow = checkLogin(() => {
    if (!post) return;
    track("follow_user", {
      action: isFollowed ? "unfollow" : "follow",
      doc_id: post?.docid,
      media_id: post?.media_id,
      type: "click"
    })
    setIsFollowed(!isFollowed);
    if (isFollowed) {
      unfollowUserService(post?.media_id);
    } else {
      followUserService(post?.media_id);
    }
  });
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };
  const [mouseEnter, setMouseEnter] = useState(false);
  const showActionIcon = mouseEnter;
  const [previewOpen, setPreviewOpen] = useState(false);
  const originTitle = useRef("");
  useEffect(() => {
    if (post) {
      if (post.title) {
        originTitle.current = document.title;
        document.title = post.title;
      }
    } else {
      document.title = originTitle.current;
    }
  }, [post]);
  // 状态改变时调用onLike回调，使用useRef避免不必要的重复调用
  const prevStateRef = useRef<{ isFollowed: boolean; isNowLiked: boolean }>({
    isFollowed: isFollowed,
    isNowLiked: isNowLiked
  });

  useEffect(() => {
    if (post && (prevStateRef.current.isFollowed !== isFollowed || prevStateRef.current.isNowLiked !== isNowLiked)) {
      onLike?.({ ...post, followed: isFollowed ? 1 : 0, isNowLiked } as Post);
      prevStateRef.current = { isFollowed, isNowLiked };
    }
  }, [isFollowed, isNowLiked, post, onLike]);
  if (!isOpen || !post) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className={clsx("absolute inset-0", {
        "modal-backdrop": !detail,
        "detail-modal-backdrop": detail
      })} onClick={onClose} />
      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl max-w-[80vw] mx-4 h-[90vh] overflow-hidden shadow-2xl animate-fade-in">
        <div className="flex h-full flex-col sm:flex-row">
          {/* Left side - Image */}
          {(post?.images || []).length > 0 ?
            <div className="flex-1 min-w-[100px] h-full bg-black" style={{
              aspectRatio: post?.coverRatio
            }}>
              <div className={`flex-1 relative h-full min-h-[300px] overflow-hidden`}
                onMouseEnter={() => setMouseEnter(true)}
                onMouseLeave={() => setMouseEnter(false)}
              >
                {(post?.images || []).length > 0 && (
                  <>
                    <div className="relative w-full flex items-center justify-center overflow-hidden" style={{
                      height: "100%"
                    }}>
                      <div className={`absolute top-[12px] right-[12px] text-white text-sm ${showActionIcon ? "opacity-100" : "opacity-0"} transition-opacity duration-300 z-[100]`}>
                        {currentImageIndex + 1}/{(post?.images || []).length}
                      </div>
                      <div className="w-full flex overflow-hidden" style={{
                        height: "100%"
                      }}>
                        {
                          (post?.images || []).map((image, index) =>
                            <div key={`${image}-${index}`} className="min-w-full max-w-full transition-transform duration-300 flex items-center justify-center" style={{
                              transform: `translateX(${-currentImageIndex * 100}%)`,
                              // minHeight: "100%",
                              // maxHeight: "100%",
                              background: `url(${image}) no-repeat center center`,
                              backgroundSize: "contain",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                            }}
                            >
                              <img
                                onClick={() => setPreviewOpen(true)}
                                src={image}
                                alt={post?.title}
                                className="object-contain transition-transform duration-300 cursor-zoom-in hidden"
                                style={{
                                  // width: "auto",
                                  // height: "auto",
                                  // maxWidth: "100%",
                                  // maxHeight: "100%",
                                }}
                                onLoad={(e) => {
                                  const img = e.target as HTMLImageElement;
                                  if (img.naturalWidth > img.naturalHeight) {
                                    img.style.width = "100%";
                                    img.style.height = "auto";
                                  } else {
                                    img.style.width = "auto";
                                    img.style.height = "100%";
                                  }
                                  img.classList.remove("hidden");

                                }}
                                ref={(imgRef) => {
                                  // 处理已缓存的图片
                                  if (imgRef && imgRef.complete && imgRef.naturalWidth > 0) {

                                    console.log('Image already loaded from cache - naturalWidth:', imgRef.naturalWidth, imgRef.naturalHeight);
                                    if (imgRef.naturalWidth > imgRef.naturalHeight) {
                                      imgRef.style.width = "100%";
                                      imgRef.style.height = "auto";
                                    } else {
                                      imgRef.style.width = "auto";
                                      imgRef.style.height = "100%";
                                    }
                                    imgRef.classList.remove("hidden");
                                  }
                                }}
                                onError={(e) => {
                                  console.log('onError---', e);
                                }}
                              />
                            </div>
                          )
                        }
                      </div>
                    </div>

                    {/* Image navigation */}
                    {(post?.images || []).length > 1 && (
                      <>
                        <button
                          disabled={currentImageIndex === 0}
                          onClick={() => {
                            handlePrevImage();
                            track("switch_image", {
                              action: "prev_image",
                              doc_id: post?.docid,
                              type: "click",
                              page: "detail"
                            })
                          }}
                          className={`absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center transition-all 
                    ${showActionIcon ? "translate-x-0" : "translate-x-[-200%]"}
                    transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <button
                          disabled={currentImageIndex === (post?.images || []).length - 1}
                          onClick={() => {
                            handleNextImage();
                            track("switch_image", {
                              action: "next_image",
                              doc_id: post?.docid,
                              type: "click",
                              page: "detail"
                            })
                          }}
                          className={`absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center transition-all 
                    ${showActionIcon ? "translate-x-0" : "translate-x-[200%]"} 
                    transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>

                        {/* Image indicators */}
                        <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 ${showActionIcon ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}>
                          {(post?.images || []).map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex
                                ? "bg-white"
                                : "bg-white bg-opacity-50"
                                }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div> :
            <div className="flex-1 h-full bg-white">
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-400 text-lg font-semibold flex items-center justify-center px-4  min-w-[350px] max-w-[500px] w-[30vw]">
                  <img src="/images/logo.png" alt="zests" className="w-[200px] h-auto" />
                </div>
              </div>
            </div>}
          {/* Right side - Content */}
          <div className="h-full overflow-y-auto relative border-l border-gray-200 min-w-[350px] max-w-[500px] w-[30vw]" >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="items-center justify-between p-4 border-b border-gray-100 hidden sm:flex">
                <div className="flex items-center space-x-3">
                  <a
                    href={`/users/${post?.media_id}`}
                    className="flex items-center space-x-3 group"
                  >
                    <img
                      src={post?.author?.avatar}
                      alt={post?.author?.username}
                      width={32}
                      height={32}
                      className="rounded-full !max-h-[32px] !max-w-[32px] min-w-[32px] min-h-[32px]"
                    />
                    <span className="font-medium text-gray-900 group-hover:text-blue-500">
                      {post?.author?.username}
                    </span>
                  </a>
                  <button className={clsx("text-sm font-medium px-4 py-1 transition-colors rounded-full", {
                    "bg-gray-100 text-gray-700 hover:bg-gray-200": isFollowed,
                    "bg-primary text-black hover:bg-primary/80": !isFollowed
                  })} onClick={handleFollow}>
                    {isFollowed ? "Following" : "+Follow"}
                  </button>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {/* Image indicators */}
                <div className="w-full h-3 flex justify-center space-x-2 opacity-0 transition-opacity duration-300 z-[100]">
                  <div className="flex items-center space-x-2 mt-1">
                    {(post?.images || []).map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-[6px] h-[6px] rounded-full transition-all ${index === currentImageIndex
                          ? "bg-indigo-600"
                          : "bg-black bg-opacity-50"
                          }`}
                      />
                    ))}
                  </div>
                </div>
                {/* Post content */}
                <div className="p-4 border-b border-gray-100">
                  <h1 className="text-xl font-semibold text-gray-900 mb-3">
                    {post?.title}
                  </h1>
                  <ContentRender content={post?.content} tags={post?.tags} />

                  {/* Location */}
                  <div className="flex items-center space-x-1 text-sm text-gray-500 mt-3">
                    {post.city_name && <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>}
                    <span>{post.city_name && `${post.city_name} • `} {post.date_str}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <div className="flex items-center space-x-6">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLike();
                      }}
                      className={`flex items-center space-x-2 text-sm transition-colors ${isNowLiked ? "text-red-500" : "text-gray-500 hover:text-red-500"
                        }`}
                    >
                      <svg
                        className={`w-5 h-5 ${isNowLiked ? "fill-current" : "fill-none"
                          }`}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                      <span>{likeCount}</span>
                    </button>

                    <button className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                      <span>{post.comments}</span>
                    </button>

                    <button className="text-gray-500 hover:text-gray-700 transition-colors hidden">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Comments */}
                <div className="flex-1 h-full pb-10">
                  <div className="p-4">
                    <CommentList
                      length={post.comments}
                      doc_id={post.docid}
                      qualityComments={qualityComments}
                    />
                  </div>
                </div>

              </div>
              <CommentInput postComment={postComment} />
            </div>
          </div>
        </div>
      </div>
      <ImagePreview src={post.images[currentImageIndex]} isOpen={previewOpen} onOpenChange={(visible) => {
        setPreviewOpen(visible);
      }} />
    </div >
  );
}

const ContentRender = ({ content, tags = [] }: { content: string, tags: { id: string, name: string }[] }) => {
  // return <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
  //   {content}
  // </p>
  // 将content中的tags高亮为a标签
  const renderContentWithTags = () => {
    if (!content || !tags || tags.length === 0) {
      return content;
    }
    // 构建正则，匹配所有tag（如 #tag）
    const tagPattern = new RegExp(
      tags
        .map((tag) => {
          // 只取#tag部分
          const tagName = tag.name.startsWith("#") ? tag.name : `#${tag.name}`;
          // 防止正则特殊字符
          return tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        })
        .join("|"),
      "g"
    );
    // 拆分文本并插入a标签
    const parts = [];
    let lastIndex = 0;
    let match;
    let idx = 0;
    while ((match = tagPattern.exec(content)) !== null) {
      const { index } = match;
      if (index > lastIndex) {
        parts.push(content.slice(lastIndex, index));
      }
      const tagText = match[0];
      const tag = tags.find((t) => t.name === tagText.replace(/^#/, ""));
      parts.push(
        <a
          target="_blank"
          key={`tag-${tagText}-${idx++}`}
          href={`/tag/${encodeURIComponent(tag?.name || "")}`}
          className="text-blue-500 hover:underline cursor-pointer"
          tabIndex={0}
          aria-label={`label: ${tag?.name}`}
        >
          {tagText}
        </a>
      );
      lastIndex = index + tagText.length;
    }
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }
    return parts;
  };

  return (
    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line post-content">
      {renderContentWithTags()}
    </p>
  );
}